import { z } from "zod";

/**
 * The analytics taxonomy (spec §11.5) — exact names, no variants. Every event goes through one `track()` helper on
 * the server; clients send only the events the server can't see itself. Property values are short tokens, numbers
 * or booleans: there is no way to put source text, question text, file names or free text into an event.
 */
export const ANALYTICS_EVENTS = [
  "signup_completed",
  "signin_completed",
  "programme_selected",
  "onboarding_completed",
  "waitlist_joined",
  "source_uploaded",
  "generation_started",
  "generation_queued_quota",
  "set_generated",
  "generation_failed",
  "review_session_started",
  "review_session_completed",
  "card_graded",
  "quiz_completed",
  "mock_exam_started",
  "mock_exam_completed",
  "tutor_question_asked",
  "tutor_not_in_material",
  "tutor_out_of_scope",
  "pro_feature_gated",
  "badge_earned",
  "mind_map_opened",
  "diagram_session_completed",
  "case_completed",
  "set_shared",
  "share_joined",
  "paywall_viewed",
  "trial_started",
  "subscription_started",
  "subscription_cancelled",
  "limit_reached",
  "flag_submitted",
  "account_deleted",
  // Usability funnel (plan BE1): where students go and where they stop.
  "screen_viewed",
  "onboarding_step_viewed",
  "onboarding_step_completed",
  "session_exited_early",
  // Study groups (plan BE15).
  "group_created",
  "group_joined",
  "group_left",
  "member_reported",
  "member_blocked",
] as const;
export const analyticsEventSchema = z.enum(ANALYTICS_EVENTS);
export type AnalyticsEvent = z.infer<typeof analyticsEventSchema>;

/** A token: lowercase identifiers like "pdf", "nursing", "mock_quick" — never prose. */
const token = z.string().regex(/^[a-z0-9_:.-]{1,40}$/, "Analytics values are short identifiers only");
const value = z.union([token, z.number().finite(), z.boolean()]);
export const analyticsPropertiesSchema = z.record(z.string().regex(/^[a-z_]{1,30}$/), value).refine((p) => Object.keys(p).length <= 12, "Too many properties");
export type AnalyticsProperties = z.infer<typeof analyticsPropertiesSchema>;

/** Events only the app knows about (screen views, client-side gates); everything else is recorded by the server. */
export const CLIENT_EVENTS = [
  "signin_completed",
  "paywall_viewed",
  "pro_feature_gated",
  "review_session_started",
  "mind_map_opened",
  "diagram_session_completed",
  "screen_viewed",
  "onboarding_step_viewed",
  "onboarding_step_completed",
  "session_exited_early",
] as const;
/** Sent before an account exists (onboarding happens first), keyed by a random per-install id instead of a user. */
export const ANONYMOUS_EVENTS = ["screen_viewed", "onboarding_step_viewed", "onboarding_step_completed"] as const;
/** Onboarding steps in order, for the funnel (mobile and web share the names where the step exists on both). */
export const ONBOARDING_STEPS = ["welcome", "showcase", "programme", "auth", "permissions"] as const;
/** A random v4 uuid generated once per install / browser; never derived from the device or the person. */
export const anonymousIdSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
export const clientTrackRequestSchema = z.object({
  event: z.enum(CLIENT_EVENTS),
  properties: analyticsPropertiesSchema.default({}),
  anonymous_id: anonymousIdSchema.optional(),
});

/**
 * Route patterns for screen_viewed, e.g. "/sets/[setId]/practice/[sessionId]" → "sets.set_id.practice.session_id".
 * Ids never reach analytics; the token stays within the 40-character property limit.
 */
export function screenToken(pattern: string): string {
  const token = pattern
    .replace(/\([^)]*\)\/?/g, "")
    .replace(/\[\.{0,3}([^\]]+)\]/g, (_, name: string) => name.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase())
    .replace(/:([a-zA-Z]+)/g, (_, name: string) => name.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase())
    .replace(/^\/+|\/+$/g, "")
    .replace(/\//g, ".")
    .replace(/[^a-z0-9_.-]/g, "");
  return (token || "root").slice(0, 40);
}
export type ClientTrackRequest = z.infer<typeof clientTrackRequestSchema>;

/** Cost bands for set_generated (§11.5): never the exact figure. */
export function costBand(usd: number): string {
  if (usd <= 0) return "free";
  if (usd < 0.01) return "lt_1c";
  if (usd < 0.05) return "lt_5c";
  if (usd < 0.25) return "lt_25c";
  return "gte_25c";
}
